// Auto-guardado de líneas sin enviar al salir de la mesa — lógica pura.
//
// Ajuste del restaurante `autoSaveDraftOnLeave` (default false). Con él ON,
// las líneas COMPLETAS sin guardar de la mesa se guardan igual que con
// «Guardar» (servido=false: KDS, sin papel) cuando:
//   - "leave"  → se sale de la mesa (deseleccionar, volver al plano, salir
//                de la carta a otra sección) o se cambia a otra mesa;
//   - "hidden" → la pestaña/app pasa a segundo plano (móvil bloqueado);
//   - "idle"   → 60 s sin tocar nada dentro de la mesa.
// Guardas: este dispositivo tiene el lease de la mesa, la sesión sigue
// abierta y no hay ningún cobro abierto. Los menús incompletos (reloj,
// «Continuar más tarde») NUNCA se envían solos: se quedan en el borrador.
// Con el ajuste OFF no se hace nada.

export const AUTO_SAVE_IDLE_MS = 60_000;

export const AUTO_SAVE_TRIGGERS = ["leave", "hidden", "idle"];

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export const isOpenSessionId = (id) => !!id && id !== EMPTY_GUID;

/** Menú/compuesto incompleto: solo lo marca «Continuar más tarde». */
export const isPartialLine = (line) => !!(line?._isPartial || line?.isPartial);

/**
 * Reparte las líneas sin enviar de un carrito/borrador:
 *   save → completas, se pueden guardar ya;
 *   keep → sin enviar que se quedan en el borrador local (incompletas).
 * Las ya enviadas (`old`) o en vuelo (`pendingSync`) no entran en ninguna.
 */
export function splitAutoSaveLines(cart) {
  const save = [];
  const keep = [];
  (cart || []).forEach((line) => {
    if (!line || line.old || line.pendingSync) return;
    if (isPartialLine(line)) keep.push(line);
    else save.push(line);
  });
  return { save, keep };
}

/** ¿El path es una pantalla de cobro? */
export function isPaymentPath(pathname) {
  const p = String(pathname || "").toLowerCase();
  return /\/(pago|payment)(\/|$)/.test(p);
}

/**
 * Decide si se auto-guarda. Devuelve `{ save: true, lines, keep }` o
 * `{ save: false, reason }`.
 */
export function decideAutoSave({
  enabled,
  trigger,
  mesaId,
  sessionId,
  holdsLease,
  leaseMode = null,
  paymentOpen = false,
  pathname = "",
  inFlight = false,
  closing = false,
  cart,
}) {
  if (!enabled) return { save: false, reason: "disabled" };
  if (!AUTO_SAVE_TRIGGERS.includes(trigger)) return { save: false, reason: "trigger" };
  if (mesaId == null || mesaId === -1 || mesaId === "-1") return { save: false, reason: "no-mesa" };
  if (!isOpenSessionId(sessionId)) return { save: false, reason: "no-session" };
  if (!holdsLease) return { save: false, reason: "no-lease" };
  if (paymentOpen || leaseMode === "charge" || isPaymentPath(pathname)) {
    return { save: false, reason: "payment" };
  }
  // La mesa se está cerrando/cobrando en este dispositivo.
  if (closing) return { save: false, reason: "payment" };
  if (inFlight) return { save: false, reason: "in-flight" };
  const { save, keep } = splitAutoSaveLines(cart);
  if (!save.length) return { save: false, reason: "nothing", keep };
  return { save: true, lines: save, keep };
}

/** «Mesa 4» para un número suelto; «Barra 1» / «Terraza 2» tal cual. */
export function mesaDisplayName(mesa) {
  const label = String(mesa?.nombre || mesa?.id_mesa || "").trim();
  if (!label) return "la mesa";
  return /^\d+$/.test(label) ? `Mesa ${label}` : label;
}

export function autoSaveSnackText(count, mesa) {
  const n = Number(count) || 0;
  const where = mesaDisplayName(mesa);
  return n === 1
    ? `Guardado automáticamente 1 producto en ${where}`
    : `Guardados automáticamente ${n} productos en ${where}`;
}

export function autoSaveFailText(mesa) {
  return `No se pudo guardar automáticamente ${mesaDisplayName(mesa)}: los productos siguen pendientes en este dispositivo`;
}
